"use client"

import { useMemo, useState } from "react"
import Link from "next/link"
import type { ModuloVendible } from "@/lib/billing/precios"
import { AREAS_EN_ORDEN, PRESET_DE_SECTOR, type SectorDeLanding } from "@/lib/landing/catalogo"
import { modulosDe, nombreDeArea, tiposDeNegocio } from "@/lib/landing/contenido"
import type { Idioma } from "@/lib/landing/idioma"
import { areaDe, euros, NUCLEO_CENTIMOS, precioMensual, siguientePack, unidadDeArea } from "@/lib/landing/precio"

/** Las dependencias duras del catálogo (lib/modules/dependencias.ts). */
const NECESITA: Partial<Record<ModuloVendible, ModuloVendible>> = { workforce: "fichaje", tickets: "facturacion" }

const T = {
  es: {
    monta: "Monta tu Zindra",
    ajusta: "Elige tu sector y ajusta",
    tipo: "1 · Tu tipo de negocio",
    tipoAria: "Tipo de negocio",
    personasPaso: "2 · Personas activas",
    personas: "Personas en el equipo",
    menos10: "Diez personas menos",
    menos1: "Una persona menos",
    mas1: "Una persona más",
    mas10: "Diez personas más",
    modulosPaso: "3 · Módulos",
    areaEntera: "Área entera: −30 %",
    pack: (n: number) => `Pack −20 % · con ${n} más, −30 %`,
    conMas: (n: number) => `Con ${n} más, −20 %`,
    nucleo: "Núcleo: empleados, centros, clientes, proveedores",
    modulo: "módulo",
    modulos: "módulos",
    personasUnidad: "personas",
    areaEnteraCorta: "área entera",
    pack2: "pack",
    alMes: "al mes",
    sinIva: "sin IVA",
    packs: "packs",
    empezar: "Empezar con estos módulos",
  },
  en: {
    monta: "Build your Zindra",
    ajusta: "Pick your industry and adjust",
    tipo: "1 · Your kind of business",
    tipoAria: "Kind of business",
    personasPaso: "2 · Active people",
    personas: "People on the team",
    menos10: "Ten people fewer",
    menos1: "One person fewer",
    mas1: "One person more",
    mas10: "Ten people more",
    modulosPaso: "3 · Modules",
    areaEntera: "Whole area: −30%",
    pack: (n: number) => `Bundle −20% · ${n} more for −30%`,
    conMas: (n: number) => `${n} more for −20%`,
    nucleo: "Core: employees, sites, customers, suppliers",
    modulo: "module",
    modulos: "modules",
    personasUnidad: "people",
    areaEnteraCorta: "whole area",
    pack2: "bundle",
    alMes: "a month",
    sinIva: "excl. VAT",
    packs: "bundles",
    empezar: "Start with these modules",
  },
}


/**
 * «Monta tu Zindra»: sector, personas y módulos, con el precio al momento.
 *
 * El precio es el que se cobra (`precioMensual` usa la misma cuenta que la
 * suscripción). El botón lleva al alta con el sector y los módulos elegidos,
 * que el alta trae marcados.
 */
export function Configurador({
  sectorInicial = "hosteleria",
  empleadosInicial = 12,
  amplio = false,
  idioma = "es",
}: {
  sectorInicial?: SectorDeLanding
  empleadosInicial?: number
  /** En /precios: pasos numerados, saltos de diez y la pista de cada pack. */
  amplio?: boolean
  idioma?: Idioma
}) {
  const t = T[idioma]
  const pc = idioma === "en" ? "%" : " %"
  const [sector, setSector] = useState<SectorDeLanding>(sectorInicial)
  const [empleados, setEmpleados] = useState(empleadosInicial)
  const [elegidos, setElegidos] = useState<Set<ModuloVendible>>(() => new Set(PRESET_DE_SECTOR[sectorInicial]))

  const precio = useMemo(() => precioMensual([...elegidos], empleados), [elegidos, empleados])

  function elegirSector(s: SectorDeLanding) {
    setSector(s)
    setElegidos(new Set(PRESET_DE_SECTOR[s]))
  }

  function alternar(m: ModuloVendible) {
    setElegidos((prev) => {
      const s = new Set(prev)
      if (s.has(m)) {
        s.delete(m)
        for (const [hijo, padre] of Object.entries(NECESITA)) if (padre === m) s.delete(hijo as ModuloVendible)
      } else {
        s.add(m)
        const padre = NECESITA[m]
        if (padre) s.add(padre)
      }
      return s
    })
  }

  const cambiar = (d: number) => setEmpleados((e) => Math.min(999, Math.max(1, e + d)))
  const alta = `/register?sector=${sector}&modulos=${[...elegidos].join(",")}`

  return (
    <div className="flex flex-col gap-5 rounded-[22px] bg-white p-6 text-[#0b0b0c] sm:p-7">
      {!amplio && (
        <div className="flex flex-wrap items-baseline justify-between gap-2">
          <span className="text-xl font-semibold tracking-tight">{t.monta}</span>
          <span className="text-[13px] text-zinc-500">{t.ajusta}</span>
        </div>
      )}

      <div className="flex flex-col gap-2.5">
        {amplio && <span className="text-[15px] font-semibold">{t.tipo}</span>}
        <div className="flex flex-wrap gap-1.5" role="group" aria-label={t.tipoAria}>
          {tiposDeNegocio(idioma).map((s) => (
            <button
              key={s.clave}
              type="button"
              aria-pressed={sector === s.clave}
              onClick={() => elegirSector(s.clave)}
              className={`min-h-10 rounded-full border px-3.5 text-sm ${
                sector === s.clave ? "border-[#0b0b0c] bg-[#0b0b0c] text-white" : "border-zinc-200 bg-white text-zinc-700 hover:border-zinc-400"
              }`}
            >
              {s.nombre}
            </button>
          ))}
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-3 rounded-2xl bg-[#f4f4f5] px-3 py-2.5">
        <span className="flex-1 text-[15px]">{amplio ? t.personasPaso : t.personas}</span>
        {amplio && (
          <button type="button" onClick={() => cambiar(-10)} aria-label={t.menos10} className="h-11 rounded-xl border border-zinc-200 bg-white px-3 text-sm">
            −10
          </button>
        )}
        <button type="button" onClick={() => cambiar(-1)} aria-label={t.menos1} className="h-11 w-11 rounded-xl border border-zinc-200 bg-white text-lg">
          −
        </button>
        <span className="min-w-12 text-center font-mono text-xl tabular-nums" aria-live="polite">
          {empleados}
        </span>
        <button type="button" onClick={() => cambiar(1)} aria-label={t.mas1} className="h-11 w-11 rounded-xl border border-zinc-200 bg-white text-lg">
          +
        </button>
        {amplio && (
          <button type="button" onClick={() => cambiar(10)} aria-label={t.mas10} className="h-11 rounded-xl border border-zinc-200 bg-white px-3 text-sm">
            +10
          </button>
        )}
      </div>

      <div className="flex flex-col gap-4">
        {amplio && <span className="text-[15px] font-semibold">{t.modulosPaso}</span>}
        {AREAS_EN_ORDEN.map((area) => {
          const mods = modulosDe(idioma).filter((m) => areaDe(m.clave) === area)
          const encendidos = mods.filter((m) => elegidos.has(m.clave)).length
          const pista = siguientePack(area, encendidos)
          return (
            <div key={area} className="flex flex-col gap-2">
              <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1">
                <span className="text-[13px] font-semibold uppercase tracking-wide">
                  {nombreDeArea(idioma, area)} <span className="font-mono text-xs font-normal normal-case tracking-normal text-zinc-500">· {unidadDeArea(area, idioma)}</span>
                </span>
                {amplio && encendidos > 0 && (
                  <span className="text-[13px] text-lime-700">
                    {pista === null
                      ? t.areaEntera
                      : encendidos >= 3
                        ? t.pack(pista.faltan)
                        : t.conMas(pista.faltan)}
                  </span>
                )}
              </div>
              <div className="flex flex-wrap gap-1.5">
                {mods.map((m) => {
                  const on = elegidos.has(m.clave)
                  return (
                    <button
                      key={m.clave}
                      type="button"
                      aria-pressed={on}
                      onClick={() => alternar(m.clave)}
                      title={m.resumen}
                      className={`inline-flex min-h-9 items-center gap-1.5 rounded-[10px] border px-3 text-sm ${
                        on ? "border-[#0b0b0c] bg-[#0b0b0c] text-white" : "border-zinc-200 bg-white text-zinc-600 hover:border-zinc-400"
                      }`}
                    >
                      <span className={`h-[7px] w-[7px] rounded-[2px] ${on ? "bg-lime-400" : "bg-zinc-300"}`} aria-hidden />
                      {m.nombre}
                    </button>
                  )
                })}
              </div>
            </div>
          )
        })}
      </div>

      <div className="flex flex-col gap-1.5 border-t border-zinc-200 pt-4 text-sm text-zinc-600">
        <div className="flex justify-between gap-3">
          <span>{t.nucleo}</span>
          <span className="font-mono tabular-nums">{euros(NUCLEO_CENTIMOS, idioma)}</span>
        </div>
        {precio.lineas.map((l) => (
          <div key={l.area} className="flex justify-between gap-3">
            <span>
              {nombreDeArea(idioma, l.area)}: {l.modulos} {l.modulos === 1 ? t.modulo : t.modulos}
              {l.porPersona ? ` × ${empleados} ${t.personasUnidad}` : ""}{" "}
              {l.pack > 0 && (
                <span className="text-lime-700">
                  · {l.pack === 30 ? t.areaEnteraCorta : t.pack2} −{l.pack}
                  {pc}
                </span>
              )}
            </span>
            <span className="font-mono tabular-nums">{euros(l.centimos, idioma)}</span>
          </div>
        ))}
      </div>

      <div className="flex flex-wrap items-center justify-between gap-4 rounded-2xl bg-[#0b0b0c] px-5 py-4 text-white">
        <div className="flex flex-col">
          <span className="font-mono text-[34px] tracking-tight text-lime-400 tabular-nums" aria-live="polite">
            {euros(precio.totalCentimos, idioma)}
          </span>
          <span className="text-[13px] text-zinc-400">
            {t.alMes} · {elegidos.size} {t.modulos} · {t.sinIva}
            {precio.brutoCentimos > precio.totalCentimos && ` · ${t.packs}: −${euros(precio.brutoCentimos - precio.totalCentimos, idioma)}`}
          </span>
        </div>
        <Link href={alta} className="rounded-xl bg-lime-400 px-4 py-3 text-[15px] font-semibold text-[#0b0b0c] hover:bg-lime-300">
          {t.empezar}
        </Link>
      </div>
    </div>
  )
}
